import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, errorMessage, newIdempotencyKey, unwrap, type EventData, type Poi } from "../../api/client";
import { useSession } from "../../auth/session";
import { formatDateTime, fromParisInput, toParisInput } from "../../domain/format";
import { fr } from "../../i18n/fr";
import type { LatLonPt } from "../../map/RouteMap";
import { Confirm, Field, Notice, useOffline } from "../../ui/components";
import { useForeignUpdate } from "../../ui/useForeignUpdate";
import { SearchBox } from "../editor/SearchBox";

interface Props {
  eventId: string; event: EventData; poi: Poi; isNew?: boolean; readOnly?: boolean; activeNow: boolean;
  onMove(): void;
  /** coordinates typed or address picked (SW-PO-04): the marker follows on the map */
  onLocationChange?(p: LatLonPt): void;
  onDone(msg?: string, saved?: Poi): void | Promise<void>; onCancel(): void;
}

/** POI edition (SW-WS-14). */
export function PoiForm({ eventId, event, poi: p0, isNew, readOnly, activeNow, onMove, onLocationChange, onDone, onCancel }: Props) {
  const s = useSession();
  const offline = useOffline();
  const [p, setP] = useState<Poi>(p0);
  useEffect(() => { setP((x) => (x.location === p0.location ? x : { ...x, location: p0.location })); }, [p0.location]);
  // SW-PO-04: coordinates editable as text, applied when both are valid numbers
  const [latText, setLatText] = useState(p0.location.coordinates[1].toFixed(5));
  const [lonText, setLonText] = useState(p0.location.coordinates[0].toFixed(5));
  useEffect(() => { setLatText(p0.location.coordinates[1].toFixed(5)); setLonText(p0.location.coordinates[0].toFixed(5)); }, [p0.location]);
  const applyCoords = (latS: string, lonS: string) => {
    // leaving a field without changing it keeps the exact position (the text shows it rounded to 5 decimals)
    if (latS === p.location.coordinates[1].toFixed(5) && lonS === p.location.coordinates[0].toFixed(5)) return;
    const lat = Number(latS.replace(",", ".")), lon = Number(lonS.replace(",", "."));
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    if (lat === p.location.coordinates[1] && lon === p.location.coordinates[0]) return;
    onLocationChange?.({ lat, lon });
  };
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Poi | null>(null);
  const [busy, setBusy] = useState(false);
  // SW-RS-14: a version saved by another operator while the form is open never erases the input in progress
  const foreign = useForeignUpdate(p0, p, isNew, (v) => setP(v), (v) => ({ ...v, location: undefined }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [idemKey] = useState(newIdempotencyKey);
  const cats = event.poiCategories ?? [];
  const canDelete = !isNew && !readOnly && (s.isAdmin || event.status === "DRAFT");
  const set = <K extends keyof Poi>(k: K, v: Poi[K]) => setP((x) => ({ ...x, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (p.validFrom && p.validTo && Date.parse(p.validTo) <= Date.parse(p.validFrom)) { setError(fr.events.endBeforeStart); return; }
    setBusy(true); setError(null); setConflict(null);
    try {
      const res = isNew
        ? unwrap(await api.POST("/events/{id}/pois", { params: { path: { id: eventId } }, body: p, headers: { "Idempotency-Key": idemKey } }))
        : unwrap(await api.PATCH("/events/{id}/pois/{pid}", { params: { path: { id: eventId, pid: p.id } }, body: p }));
      await onDone(res.zoneExtended ? `${fr.poiForm.saved} ${fr.workspace.zoneExtended}` : fr.poiForm.saved, res.item);
    } catch (x) {
      if (x instanceof ApiError && x.code === "VERSION_CONFLICT" && x.current) setConflict(x.current as Poi);
      setError(errorMessage(x, fr.errors));
    } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      unwrap(await api.DELETE("/events/{id}/pois/{pid}", { params: { path: { id: eventId, pid: p.id } } }));
      await onDone(fr.poiForm.deleted);
    } catch (x) { setError(errorMessage(x, fr.errors)); setConfirmDelete(false); } finally { setBusy(false); }
  };

  return (
    <form className="card stack form" onSubmit={submit}>
      <div className="row between">
        <h3>{isNew ? fr.poiForm.new : p0.name}</h3>
        <button type="button" className="icon" onClick={onCancel} aria-label={fr.common.close}>×</button>
      </div>
      {activeNow && !readOnly && <Notice kind="warn">{fr.workspace.activeWarning}</Notice>}
      <Field label={fr.poiForm.name}><input value={p.name} onChange={(e) => set("name", e.target.value)} required maxLength={120} disabled={readOnly} autoFocus={isNew} /></Field>
      <Field label={fr.poiForm.category}>
        <select value={p.categoryId} onChange={(e) => set("categoryId", e.target.value)} required disabled={readOnly}>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}{c.hidden ? ` (${fr.categories.hidden.toLowerCase()})` : ""}</option>)}
        </select>
      </Field>
      <div className="field"><span>{fr.poiForm.position}</span>
        <div className="row between small">
          <span className="row tight">
            <input type="text" inputMode="decimal" className="coord" aria-label={fr.poiForm.lat} value={latText} disabled={readOnly || !onLocationChange}
              onChange={(e) => setLatText(e.target.value)} onBlur={() => applyCoords(latText, lonText)} />
            <input type="text" inputMode="decimal" className="coord" aria-label={fr.poiForm.lon} value={lonText} disabled={readOnly || !onLocationChange}
              onChange={(e) => setLonText(e.target.value)} onBlur={() => applyCoords(latText, lonText)} />
          </span>
          {!readOnly && <button type="button" onClick={onMove}>{fr.poiForm.move}</button>}
        </div>
        {!readOnly && onLocationChange && (
          <div className="small">
            <span className="muted">{fr.poiForm.placeAt} : </span>
            <SearchBox near={{ lat: p.location.coordinates[1], lon: p.location.coordinates[0] }} placeholder={fr.poiForm.placeAtHint} onPick={(pt) => onLocationChange(pt)} />
          </div>
        )}
      </div>
      <div className="grid2">
        <Field label={fr.poiForm.validFrom} hint={fr.common.optional}><input type="datetime-local" value={p.validFrom ? toParisInput(p.validFrom) : ""} disabled={readOnly} onChange={(e) => set("validFrom", e.target.value ? fromParisInput(e.target.value) : undefined)} /></Field>
        <Field label={fr.poiForm.validTo} hint={fr.common.optional}><input type="datetime-local" value={p.validTo ? toParisInput(p.validTo) : ""} disabled={readOnly} onChange={(e) => set("validTo", e.target.value ? fromParisInput(e.target.value) : undefined)} /></Field>
      </div>
      <Field label={fr.poiForm.details}><textarea rows={3} value={p.details ?? ""} disabled={readOnly} onChange={(e) => set("details", e.target.value)} /></Field>
      {foreign.changed && !conflict && (
        <Notice kind="warn">
          {fr.common.changedMeanwhile(foreign.changed.updatedBy, foreign.changed.updatedAt ? formatDateTime(foreign.changed.updatedAt) : undefined)}
          <button type="button" onClick={() => { setP(foreign.changed!); foreign.dismiss(); }}>{fr.common.reloadCurrent}</button>
        </Notice>
      )}
      {conflict && (
        <Notice kind="warn">
          {fr.common.conflict}
          <div className="small">{fr.common.version} {conflict.version}{conflict.updatedBy ? ` ${fr.common.by} ${conflict.updatedBy}` : ""}{conflict.updatedAt ? `, ${formatDateTime(conflict.updatedAt)}` : ""}</div>
          <button type="button" onClick={() => { setP(conflict); setConflict(null); setError(null); foreign.dismiss(); }}>{fr.common.reloadCurrent}</button>
        </Notice>
      )}
      {error && !conflict && <div className="error small">{error}</div>}
      {!readOnly && (
        <div className="row between wrap">
          <div>{canDelete && <button type="button" className="danger ghost" onClick={() => setConfirmDelete(true)}>{fr.common.delete}</button>}</div>
          <div className="row">
            <button type="button" onClick={onCancel}>{fr.common.cancel}</button>
            <button type="submit" className="primary" disabled={busy || offline || !p.categoryId} title={offline ? fr.common.offlineShort : undefined}>{busy ? fr.common.saving : fr.common.save}</button>
          </div>
        </div>
      )}
      <Confirm open={confirmDelete} title={fr.common.delete} text={fr.poiForm.deleteConfirm} danger confirmLabel={fr.common.delete} onConfirm={del} onClose={() => setConfirmDelete(false)} />
    </form>
  );
}
